'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth-context';
import { Button, Input, Badge, Card, Avatar } from '@/components/ds';

/** Roles that can create/edit/assign services / manage schedules for staff. */
const EDIT_ROLES = ['manager', 'business_owner'] as const;

/** Blank state for the create/edit form. */
const EMPTY_FORM = {
  displayName: '',
  isActive: true,
};

type FormState = typeof EMPTY_FORM;

type StaffRow = {
  id: string;
  displayName: string;
  userId: string | null;
  isActive: boolean;
  serviceIds: string[];
};

type ServiceRow = {
  id: string;
  name: string;
};

// ── Schedule Editor ──────────────────────────────────────────────────────────

type SlotDraft = { dayOfWeek: number; startTime: string; endTime: string };

interface SchedulePanelProps {
  staffId: string;
  staffName: string;
  canEdit: boolean;
  onClose: () => void;
}

/**
 * Schedule editor panel for one staff member (T20b).
 *
 * - Manager/owner: full edit (setWorkingHours, setBreaks, addTimeOff, removeTimeOff).
 * - Staff role: read-only view (no save/add/remove controls).
 *
 * Time columns are stored as 'HH:MM' strings; timestamptz as ISO UTC.
 * day_of_week 0 = Sunday per T10/T15 convention.
 */
function SchedulePanel({ staffId, staffName, canEdit, onClose }: SchedulePanelProps) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();

  const scheduleQuery = trpc.staff.getSchedule.useQuery({ staffId }, { retry: 1 });

  // Local editable state — null means "not yet edited, fall back to query data"
  const [localHours, setLocalHours] = useState<SlotDraft[] | null>(null);
  const [localBreaks, setLocalBreaks] = useState<SlotDraft[] | null>(null);

  // Add-row forms for working hours and breaks
  const [newHour, setNewHour] = useState<SlotDraft>({
    dayOfWeek: 0,
    startTime: '09:00',
    endTime: '17:00',
  });
  const [newBreak, setNewBreak] = useState<SlotDraft>({
    dayOfWeek: 0,
    startTime: '12:00',
    endTime: '13:00',
  });

  // Add-time-off form
  const [newTimeOff, setNewTimeOff] = useState({ startsAt: '', endsAt: '', reason: '' });

  const invalidateSchedule = () => void utils.staff.getSchedule.invalidate({ staffId });

  const setWorkingHoursMutation = trpc.staff.setWorkingHours.useMutation({
    onSuccess: () => {
      setLocalHours(null);
      invalidateSchedule();
    },
  });
  const setBreaksMutation = trpc.staff.setBreaks.useMutation({
    onSuccess: () => {
      setLocalBreaks(null);
      invalidateSchedule();
    },
  });
  const addTimeOffMutation = trpc.staff.addTimeOff.useMutation({
    onSuccess: () => {
      setNewTimeOff({ startsAt: '', endsAt: '', reason: '' });
      invalidateSchedule();
    },
  });
  const removeTimeOffMutation = trpc.staff.removeTimeOff.useMutation({
    onSuccess: invalidateSchedule,
  });

  const DAY_LABELS: string[] = [
    t('staff.day_sun'),
    t('staff.day_mon'),
    t('staff.day_tue'),
    t('staff.day_wed'),
    t('staff.day_thu'),
    t('staff.day_fri'),
    t('staff.day_sat'),
  ];

  // Derive displayed hours/breaks: local edits take precedence over server data
  const serverHours =
    scheduleQuery.data?.workingHours.map((wh) => ({
      dayOfWeek: wh.dayOfWeek,
      startTime: wh.startTime,
      endTime: wh.endTime,
    })) ?? [];
  const displayedHours = localHours ?? serverHours;

  const serverBreaks =
    scheduleQuery.data?.breaks.map((b) => ({
      dayOfWeek: b.dayOfWeek,
      startTime: b.startTime,
      endTime: b.endTime,
    })) ?? [];
  const displayedBreaks = localBreaks ?? serverBreaks;

  const handleSaveHours = () => {
    setWorkingHoursMutation.mutate({ staffId, hours: displayedHours });
  };

  const handleSaveBreaks = () => {
    setBreaksMutation.mutate({ staffId, breaks: displayedBreaks });
  };

  const handleAddTimeOff = () => {
    if (!newTimeOff.startsAt || !newTimeOff.endsAt) return;
    // Pass the raw datetime-local wall-clock strings (YYYY-MM-DDTHH:MM).
    // The server interprets them as Asia/Jerusalem and converts to UTC — DST-safe.
    addTimeOffMutation.mutate({
      staffId,
      startsAtLocal: newTimeOff.startsAt,
      endsAtLocal: newTimeOff.endsAt,
      reason: newTimeOff.reason || undefined,
    });
  };

  /** Shared style for native time/select inputs (LTR, latin clock numerals). */
  const nativeInputStyle: React.CSSProperties = {
    height: 'var(--control-h)',
    paddingInline: 'var(--sp-5)',
    border: '1px solid var(--border-strong)',
    borderRadius: 'var(--r-sm)',
    background: 'var(--bg-surface)',
    fontFamily: 'var(--font-sans)',
    fontSize: 'var(--fs-body)',
    color: 'var(--text-primary)',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--sp-3)',
    marginBlockEnd: '0.375rem',
    flexWrap: 'wrap',
  };

  return (
    <div aria-label={`${t('staff.manage_schedule')} ${staffName}`}>
      <Card padding={20} style={{ marginBlockEnd: '1.5rem' }}>
        {/* Panel header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBlockEnd: '1rem',
          }}
        >
          <h2 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            {t('staff.manage_schedule')}: {staffName}
          </h2>
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            {t('staff.cancel')}
          </Button>
        </div>

        {!canEdit && (
          <p
            role="status"
            style={{
              background: 'var(--warn-wash)',
              border: '1px solid var(--warn)',
              borderRadius: 'var(--r-sm)',
              padding: '0.4rem 0.6rem',
              marginBlockEnd: '1rem',
              fontSize: 'var(--fs-caption)',
            }}
          >
            {t('staff.read_only_notice')}
          </p>
        )}

        {scheduleQuery.isPending && <p>{t('common.loading')}</p>}
        {scheduleQuery.error && (
          <p role="alert" style={{ color: 'var(--error)' }}>
            {scheduleQuery.error.message}
          </p>
        )}

        {scheduleQuery.data && (
          <>
            {/* ── Working Hours ── */}
            <section aria-label={t('staff.working_hours')} style={{ marginBlockEnd: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  marginBlockEnd: '0.5rem',
                  color: 'var(--text-primary)',
                }}
              >
                {t('staff.working_hours')}
              </h3>

              {displayedHours.length === 0 && (
                <p
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockEnd: '0.5rem',
                  }}
                >
                  -
                </p>
              )}

              {displayedHours.map((h, i) => (
                <div key={i} style={rowStyle}>
                  <span
                    style={{
                      minInlineSize: '4rem',
                      fontSize: 'var(--fs-caption)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {DAY_LABELS[h.dayOfWeek]}
                  </span>
                  {/* Single text node — contiguous for getByText(/09:00\s*-\s*17:00/) */}
                  <span
                    dir="ltr"
                    style={{
                      fontSize: 'var(--fs-body)',
                      color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {h.startTime} - {h.endTime}
                  </span>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => setLocalHours(displayedHours.filter((_, idx) => idx !== i))}
                      aria-label={`${t('staff.remove')} ${DAY_LABELS[h.dayOfWeek]} ${h.startTime}-${h.endTime}`}
                    >
                      {t('staff.remove')}
                    </Button>
                  )}
                </div>
              ))}

              {canEdit && (
                <>
                  <div style={{ ...rowStyle, marginBlockStart: '0.5rem' }}>
                    <select
                      value={newHour.dayOfWeek}
                      onChange={(e) =>
                        setNewHour((prev) => ({ ...prev, dayOfWeek: parseInt(e.target.value, 10) }))
                      }
                      aria-label={t('staff.day_of_week_label')}
                      style={nativeInputStyle}
                    >
                      {DAY_LABELS.map((label, i) => (
                        <option key={i} value={i}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={newHour.startTime}
                      onChange={(e) =>
                        setNewHour((prev) => ({ ...prev, startTime: e.target.value }))
                      }
                      aria-label={t('staff.start_time_label')}
                      dir="ltr"
                      style={nativeInputStyle}
                    />
                    <input
                      type="time"
                      value={newHour.endTime}
                      onChange={(e) => setNewHour((prev) => ({ ...prev, endTime: e.target.value }))}
                      aria-label={t('staff.end_time_label')}
                      dir="ltr"
                      style={nativeInputStyle}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setLocalHours([...displayedHours, { ...newHour }])}
                      aria-label={`${t('staff.add_row')} ${t('staff.working_hours')}`}
                    >
                      {t('staff.add_row')}
                    </Button>
                  </div>

                  {setWorkingHoursMutation.error && (
                    <p
                      role="alert"
                      style={{
                        color: 'var(--error)',
                        fontSize: 'var(--fs-caption)',
                        marginBlockStart: '0.25rem',
                      }}
                    >
                      {setWorkingHoursMutation.error.message}
                    </p>
                  )}

                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSaveHours}
                    disabled={setWorkingHoursMutation.isPending}
                    aria-label={t('staff.save_hours')}
                    style={{ marginBlockStart: '0.5rem' }}
                  >
                    {setWorkingHoursMutation.isPending ? t('staff.saving') : t('staff.save_hours')}
                  </Button>
                </>
              )}
            </section>

            {/* ── Breaks ── */}
            <section aria-label={t('staff.breaks_title')} style={{ marginBlockEnd: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  marginBlockEnd: '0.5rem',
                  color: 'var(--text-primary)',
                }}
              >
                {t('staff.breaks_title')}
              </h3>

              {displayedBreaks.length === 0 && (
                <p
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockEnd: '0.5rem',
                  }}
                >
                  -
                </p>
              )}

              {displayedBreaks.map((b, i) => (
                <div key={i} style={rowStyle}>
                  <span
                    style={{
                      minInlineSize: '4rem',
                      fontSize: 'var(--fs-caption)',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    {DAY_LABELS[b.dayOfWeek]}
                  </span>
                  {/* Single text node — contiguous for getByText(/12:00\s*-\s*13:00/) */}
                  <span
                    dir="ltr"
                    style={{
                      fontSize: 'var(--fs-body)',
                      color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {b.startTime} - {b.endTime}
                  </span>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => setLocalBreaks(displayedBreaks.filter((_, idx) => idx !== i))}
                      aria-label={`${t('staff.remove')} ${DAY_LABELS[b.dayOfWeek]} ${b.startTime}-${b.endTime} ${t('staff.breaks_title')}`}
                    >
                      {t('staff.remove')}
                    </Button>
                  )}
                </div>
              ))}

              {canEdit && (
                <>
                  <div style={{ ...rowStyle, marginBlockStart: '0.5rem' }}>
                    <select
                      value={newBreak.dayOfWeek}
                      onChange={(e) =>
                        setNewBreak((prev) => ({
                          ...prev,
                          dayOfWeek: parseInt(e.target.value, 10),
                        }))
                      }
                      aria-label={`${t('staff.day_of_week_label')} ${t('staff.breaks_title')}`}
                      style={nativeInputStyle}
                    >
                      {DAY_LABELS.map((label, i) => (
                        <option key={i} value={i}>
                          {label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={newBreak.startTime}
                      onChange={(e) =>
                        setNewBreak((prev) => ({ ...prev, startTime: e.target.value }))
                      }
                      aria-label={`${t('staff.start_time_label')} ${t('staff.breaks_title')}`}
                      dir="ltr"
                      style={nativeInputStyle}
                    />
                    <input
                      type="time"
                      value={newBreak.endTime}
                      onChange={(e) =>
                        setNewBreak((prev) => ({ ...prev, endTime: e.target.value }))
                      }
                      aria-label={`${t('staff.end_time_label')} ${t('staff.breaks_title')}`}
                      dir="ltr"
                      style={nativeInputStyle}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => setLocalBreaks([...displayedBreaks, { ...newBreak }])}
                      aria-label={`${t('staff.add_row')} ${t('staff.breaks_title')}`}
                    >
                      {t('staff.add_row')}
                    </Button>
                  </div>

                  {setBreaksMutation.error && (
                    <p
                      role="alert"
                      style={{
                        color: 'var(--error)',
                        fontSize: 'var(--fs-caption)',
                        marginBlockStart: '0.25rem',
                      }}
                    >
                      {setBreaksMutation.error.message}
                    </p>
                  )}

                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleSaveBreaks}
                    disabled={setBreaksMutation.isPending}
                    aria-label={t('staff.save_breaks')}
                    style={{ marginBlockStart: '0.5rem' }}
                  >
                    {setBreaksMutation.isPending ? t('staff.saving') : t('staff.save_breaks')}
                  </Button>
                </>
              )}
            </section>

            {/* ── Time Off ── */}
            <section aria-label={t('staff.time_off_title')} style={{ marginBlockEnd: '1.5rem' }}>
              <h3
                style={{
                  fontSize: '0.9375rem',
                  fontWeight: 600,
                  marginBlockEnd: '0.5rem',
                  color: 'var(--text-primary)',
                }}
              >
                {t('staff.time_off_title')}
              </h3>

              {scheduleQuery.data.timeOff.length === 0 && (
                <p
                  style={{
                    color: 'var(--text-tertiary)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockEnd: '0.5rem',
                  }}
                >
                  {t('staff.no_time_off')}
                </p>
              )}

              {scheduleQuery.data.timeOff.map((to) => (
                <div key={to.id} style={rowStyle}>
                  <span
                    dir="ltr"
                    style={{
                      fontSize: 'var(--fs-body)',
                      color: 'var(--text-primary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {new Date(to.startsAt).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                    {' - '}
                    {new Date(to.endsAt).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                    {to.reason ? ` (${to.reason})` : ''}
                  </span>
                  {canEdit && (
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeTimeOffMutation.mutate({ id: to.id })}
                      disabled={removeTimeOffMutation.isPending}
                      aria-label={`${t('staff.remove_time_off')} ${to.id}`}
                    >
                      {t('staff.remove')}
                    </Button>
                  )}
                </div>
              ))}

              {canEdit && (
                <div style={{ marginBlockStart: '0.75rem' }}>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 'var(--sp-4)',
                      marginBlockEnd: 'var(--sp-3)',
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                      <label
                        htmlFor="time-off-starts"
                        style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-secondary)' }}
                      >
                        {t('staff.starts_at_label')}
                      </label>
                      <input
                        id="time-off-starts"
                        type="datetime-local"
                        value={newTimeOff.startsAt}
                        onChange={(e) =>
                          setNewTimeOff((prev) => ({ ...prev, startsAt: e.target.value }))
                        }
                        aria-label={t('staff.starts_at_label')}
                        style={nativeInputStyle}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                      <label
                        htmlFor="time-off-ends"
                        style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-secondary)' }}
                      >
                        {t('staff.ends_at_label')}
                      </label>
                      <input
                        id="time-off-ends"
                        type="datetime-local"
                        value={newTimeOff.endsAt}
                        onChange={(e) =>
                          setNewTimeOff((prev) => ({ ...prev, endsAt: e.target.value }))
                        }
                        aria-label={t('staff.ends_at_label')}
                        style={nativeInputStyle}
                      />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2)' }}>
                      <label
                        htmlFor="time-off-reason"
                        style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-secondary)' }}
                      >
                        {t('staff.reason_label')}
                      </label>
                      <input
                        id="time-off-reason"
                        type="text"
                        value={newTimeOff.reason}
                        onChange={(e) =>
                          setNewTimeOff((prev) => ({ ...prev, reason: e.target.value }))
                        }
                        aria-label={t('staff.reason_label')}
                        placeholder={t('staff.reason_label')}
                        style={{ ...nativeInputStyle, minInlineSize: '10rem' }}
                      />
                    </div>
                  </div>

                  {addTimeOffMutation.error && (
                    <p
                      role="alert"
                      style={{
                        color: 'var(--error)',
                        fontSize: 'var(--fs-caption)',
                        marginBlockStart: '0.25rem',
                      }}
                    >
                      {addTimeOffMutation.error.message}
                    </p>
                  )}

                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleAddTimeOff}
                    disabled={
                      addTimeOffMutation.isPending || !newTimeOff.startsAt || !newTimeOff.endsAt
                    }
                    aria-label={t('staff.add_time_off')}
                    style={{ marginBlockStart: '0.5rem' }}
                  >
                    {addTimeOffMutation.isPending ? t('staff.saving') : t('staff.add_time_off')}
                  </Button>
                </div>
              )}
            </section>
          </>
        )}
      </Card>
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function StaffPage() {
  const { t } = useTranslation();
  const auth = useAuth();

  const canEdit = EDIT_ROLES.includes(
    (auth.activeBusiness?.role ?? '') as (typeof EDIT_ROLES)[number],
  );

  const staffQuery = trpc.staff.list.useQuery(undefined, { retry: 1 });
  const serviceQuery = trpc.service.list.useQuery({ includeArchived: false }, { retry: 1 });
  const utils = trpc.useUtils();

  const invalidateLists = () => {
    void utils.staff.list.invalidate();
  };

  const createMutation = trpc.staff.create.useMutation({ onSuccess: invalidateLists });
  const updateMutation = trpc.staff.update.useMutation({ onSuccess: invalidateLists });
  const setServicesMutation = trpc.staff.setServices.useMutation({ onSuccess: invalidateLists });

  // Form visibility: null = hidden; 'create' = new form; string (id) = edit form.
  const [formMode, setFormMode] = useState<null | 'create' | string>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Per-staff service assignment state: staffId → set of selected serviceIds.
  const [assigningStaffId, setAssigningStaffId] = useState<string | null>(null);
  const [assignedServiceIds, setAssignedServiceIds] = useState<Set<string>>(new Set());

  // Schedule panel state: which staff member's schedule is open.
  const [schedulingStaffId, setSchedulingStaffId] = useState<string | null>(null);
  const [schedulingStaffName, setSchedulingStaffName] = useState<string>('');

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setDisplayNameError(null);
    setSuccessMsg(null);
    setFormMode('create');
    setAssigningStaffId(null);
    setSchedulingStaffId(null);
  };

  const openEdit = (staff: StaffRow) => {
    setForm({ displayName: staff.displayName, isActive: staff.isActive });
    setDisplayNameError(null);
    setSuccessMsg(null);
    setFormMode(staff.id);
    setAssigningStaffId(null);
    setSchedulingStaffId(null);
  };

  const closeForm = () => {
    setFormMode(null);
    setDisplayNameError(null);
  };

  const openAssign = (staff: StaffRow) => {
    setAssigningStaffId(staff.id);
    setAssignedServiceIds(new Set(staff.serviceIds));
    setFormMode(null);
    setSchedulingStaffId(null);
  };

  const closeAssign = () => {
    setAssigningStaffId(null);
    setAssignedServiceIds(new Set());
  };

  const openSchedule = (staff: StaffRow) => {
    setSchedulingStaffId(staff.id);
    setSchedulingStaffName(staff.displayName);
    setFormMode(null);
    setAssigningStaffId(null);
  };

  const closeSchedule = () => {
    setSchedulingStaffId(null);
    setSchedulingStaffName('');
  };

  const handleDisplayNameChange = (value: string) => {
    setForm((prev) => ({ ...prev, displayName: value }));
    if (displayNameError) setDisplayNameError(null);
  };

  const validateForm = (): boolean => {
    if (!form.displayName.trim()) {
      setDisplayNameError(t('staff.display_name_required'));
      return false;
    }
    return true;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    if (!validateForm()) return;

    const displayName = form.displayName.trim();

    if (formMode === 'create') {
      createMutation.mutate(
        { displayName },
        {
          onSuccess: () => {
            setSuccessMsg(t('staff.success_created'));
            closeForm();
          },
        },
      );
    } else if (formMode) {
      // Only send isActive when it actually changed — sending it unconditionally
      // re-enters the revoke path (staff.update's isActive:false branch) on
      // every save, even a pure display-name edit on an already-inactive row.
      // Post-R13 this can't 403 (a manager/owner/secretary-linked row keeps its
      // membership either way), but it can still silently delete a re-invited
      // plain `staff` membership on a row that was already inactive — the name
      // edit alone must not re-trigger that revoke. Mirrors
      // apps/mobile/app/business-staff.tsx.
      const original = staffList.find((s) => s.id === formMode);
      const fields: { displayName: string; isActive?: boolean } = { displayName };
      if (original === undefined || form.isActive !== original.isActive) {
        fields.isActive = form.isActive;
      }
      updateMutation.mutate(
        { id: formMode, ...fields },
        {
          onSuccess: () => {
            setSuccessMsg(t('staff.success_updated'));
            closeForm();
          },
        },
      );
    }
  };

  const toggleServiceAssignment = (serviceId: string) => {
    setAssignedServiceIds((prev) => {
      const next = new Set(prev);
      if (next.has(serviceId)) {
        next.delete(serviceId);
      } else {
        next.add(serviceId);
      }
      return next;
    });
  };

  const handleSaveServices = () => {
    if (!assigningStaffId) return;
    setServicesMutation.mutate(
      { staffId: assigningStaffId, serviceIds: [...assignedServiceIds] },
      { onSuccess: closeAssign },
    );
  };

  const isMutating = createMutation.isPending || updateMutation.isPending;
  const mutationError = createMutation.error ?? updateMutation.error;

  const staffList: StaffRow[] = staffQuery.data ?? [];
  const serviceList: ServiceRow[] = (serviceQuery.data ?? []) as ServiceRow[];

  return (
    <div style={{ maxInlineSize: '720px', marginInline: 'auto', padding: '2rem 1rem' }}>
      {/* Header row */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBlockEnd: '1.5rem',
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{t('staff.title')}</h1>
        {canEdit &&
          formMode === null &&
          assigningStaffId === null &&
          schedulingStaffId === null && (
            <Button
              variant="primary"
              size="sm"
              onClick={openCreate}
              aria-label={t('staff.new_staff')}
              data-testid="add-staff"
            >
              {t('staff.new_staff')}
            </Button>
          )}
      </div>

      {/* Read-only notice for non-editors */}
      {!canEdit && (
        <p
          role="status"
          style={{
            background: 'var(--warn-wash)',
            border: '1px solid var(--warn)',
            borderRadius: 'var(--r-sm)',
            padding: '0.5rem 0.75rem',
            marginBlockEnd: '1rem',
            fontSize: 'var(--fs-caption)',
          }}
        >
          {t('staff.read_only_notice')}
        </p>
      )}

      {/* Success banner */}
      {successMsg && (
        <p role="status" style={{ color: 'var(--success)', marginBlockEnd: '1rem' }}>
          {successMsg}
        </p>
      )}

      {/* ---- Create / Edit form ---- */}
      {formMode !== null && canEdit && (
        <form
          onSubmit={handleSubmit}
          aria-label={formMode === 'create' ? t('staff.new_staff') : t('staff.edit_staff')}
          style={{ marginBlockEnd: '1.5rem' }}
        >
          <Card padding={20}>
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                marginBlockEnd: '1rem',
                color: 'var(--text-primary)',
              }}
            >
              {formMode === 'create' ? t('staff.new_staff') : t('staff.edit_staff')}
            </h2>

            {/* Display name */}
            <div style={{ marginBlockEnd: '1rem' }}>
              <Input
                label={t('staff.display_name_label')}
                value={form.displayName}
                onChange={(e) => handleDisplayNameChange(e.target.value)}
                disabled={isMutating}
                aria-describedby={displayNameError ? 'staff-display-name-error' : undefined}
              />
              {displayNameError && (
                <p
                  id="staff-display-name-error"
                  role="alert"
                  style={{
                    color: 'var(--error)',
                    fontSize: 'var(--fs-caption)',
                    marginBlockStart: 'var(--sp-2)',
                  }}
                >
                  {displayNameError}
                </p>
              )}
            </div>

            {/* Active toggle — only shown in edit mode */}
            {formMode !== 'create' && (
              <div style={{ marginBlockEnd: '1rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    cursor: 'pointer',
                    fontSize: 'var(--fs-body)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                    disabled={isMutating}
                  />
                  {form.isActive ? t('staff.active_badge') : t('staff.inactive_badge')}
                </label>
              </div>
            )}

            {/* Mutation error */}
            {mutationError && (
              <p role="alert" style={{ color: 'var(--error)', marginBlockEnd: '0.75rem' }}>
                {mutationError.message}
              </p>
            )}

            {/* Form actions */}
            <div
              style={{
                display: 'flex',
                gap: 'var(--sp-3)',
                flexWrap: 'wrap',
              }}
            >
              <Button type="submit" variant="primary" size="sm" disabled={isMutating}>
                {isMutating ? t('staff.saving') : t('staff.save')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={closeForm}
                disabled={isMutating}
              >
                {t('staff.cancel')}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {/* ---- Service assignment panel ---- */}
      {assigningStaffId !== null && canEdit && (
        <div aria-label={t('staff.assign_services')} style={{ marginBlockEnd: '1.5rem' }}>
          <Card padding={20}>
            <h2
              style={{
                fontSize: '1rem',
                fontWeight: 600,
                marginBlockEnd: '1rem',
                color: 'var(--text-primary)',
              }}
            >
              {t('staff.assign_services')}
            </h2>

            {serviceList.length === 0 && (
              <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--fs-caption)' }}>-</p>
            )}

            {/* Keep native checkboxes — tests use getByRole('checkbox', {name:/Haircut/}) */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-3)',
                marginBlockEnd: '1rem',
              }}
            >
              {serviceList.map((svc) => (
                <label
                  key={svc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-3)',
                    cursor: 'pointer',
                    fontSize: 'var(--fs-body)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={assignedServiceIds.has(svc.id)}
                    onChange={() => toggleServiceAssignment(svc.id)}
                  />
                  {svc.name}
                </label>
              ))}
            </div>

            {setServicesMutation.error && (
              <p role="alert" style={{ color: 'var(--error)', marginBlockEnd: '0.75rem' }}>
                {setServicesMutation.error.message}
              </p>
            )}

            <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleSaveServices}
                disabled={setServicesMutation.isPending}
                aria-label={t('staff.save_services')}
              >
                {setServicesMutation.isPending ? t('staff.saving') : t('staff.save_services')}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={closeAssign}
                disabled={setServicesMutation.isPending}
              >
                {t('staff.cancel')}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* ---- Schedule editor panel ---- */}
      {schedulingStaffId !== null && (
        <SchedulePanel
          staffId={schedulingStaffId}
          staffName={schedulingStaffName}
          canEdit={canEdit}
          onClose={closeSchedule}
        />
      )}

      {/* ---- Staff list ---- */}
      {staffQuery.isPending && <p>{t('common.loading')}</p>}

      {staffQuery.error && (
        <p role="alert" style={{ color: 'var(--error)' }}>
          {t('common.error')}: {staffQuery.error.message}
        </p>
      )}

      {staffQuery.data && staffList.length === 0 && (
        <p style={{ color: 'var(--text-tertiary)' }}>{t('staff.no_staff')}</p>
      )}

      {staffQuery.data && staffList.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          {staffList.map((member, idx) => {
            const memberServices = serviceList.filter((s) => member.serviceIds.includes(s.id));
            return (
              <Card
                key={member.id}
                data-testid="staff-card"
                padding={0}
                style={{ opacity: member.isActive ? 1 : 0.6 }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--sp-4)',
                    padding: '12px 16px',
                    flexWrap: 'wrap',
                  }}
                >
                  {/* Avatar with staff color ring */}
                  <Avatar name={member.displayName} ring={(idx % 8) + 1} size={40} />

                  {/* Staff info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--fs-body-l)',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {member.displayName}
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--fs-caption)',
                        color: 'var(--text-secondary)',
                        marginTop: 2,
                      }}
                    >
                      {memberServices.length === 0
                        ? '-'
                        : memberServices.map((s) => s.name).join(', ')}
                    </div>
                  </div>

                  {/* Status badge */}
                  <Badge tone={member.isActive ? 'success' : 'neutral'}>
                    {member.isActive ? t('staff.active_badge') : t('staff.inactive_badge')}
                  </Badge>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
                    {canEdit && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openEdit(member)}
                          aria-label={`${t('staff.edit_staff')} ${member.displayName}`}
                        >
                          {t('staff.edit_staff')}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openAssign(member)}
                          aria-label={`${t('staff.assign_services')} ${member.displayName}`}
                        >
                          {t('staff.assign_services')}
                        </Button>
                      </>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openSchedule(member)}
                      aria-label={`${t('staff.manage_schedule')} ${member.displayName}`}
                    >
                      {t('staff.manage_schedule')}
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
